"use client";

/**
 * Untermenü „Geführte Touren“ im Konto-Menü (Musterkatalog M-017 TO4, Design-Guide `tour-overlay.md`, `konto-menue.md`).
 * Die Tourliste kommt vom Aufrufer (Registry der App, nur Anzeigedaten: Id, Titel, Kapitelzeile, Gruppe); die Auswahl
 * startet die Tour über das Fenster-Ereignis `horn:tour:start` mit der Tour-Id (derselbe Weg wie `startTour()` der
 * Altflächen). Das Menü kennt weder Registry noch Router; der Host der App löst die Id auf und zeigt den Overlay.
 * Keine Server-Logik, keine Rechte, nichts wird gespeichert oder gesendet (r177).
 */
export interface KontoTour {
  id: string;
  titel: string;
  /** Kapitelzeile vor dem Titel, z. B. „Kapitel 3“ (Übungs-Touren). */
  zusatz?: string;
  /** Überschrift der Gruppe; aufeinanderfolgende Touren mit derselben Gruppe stehen unter einer Überschrift. */
  gruppe?: string;
}

export const TOUR_START_EREIGNIS = "horn:tour:start";
/** Id des Konto-Auslösers (Fokus-Rückgabe nach der Tour). */
export const KONTO_AUSLOESER_ID = "hs-topbar-konto";

const EINTRAG_VOLL =
  "w-full text-start flex items-center gap-x-2 py-2 px-3 rounded-lg text-sm text-dropdown-item-foreground hover:bg-dropdown-item-hover disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:bg-dropdown-item-focus";

export function starteTour(tourId: string): void {
  window.dispatchEvent(
    new CustomEvent(TOUR_START_EREIGNIS, { detail: { tourId, ausloeser: document.getElementById(KONTO_AUSLOESER_ID) } }),
  );
}

/**
 * Schließt das Konto-Menü (und damit das Untermenü) über die Preline-API (`HSDropdown.close`), sobald die Tour startet; sonst
 * bliebe es (`--auto-close: inside`) hinter dem Einleitungs-Dimmer offen. Fehlt Preline oder die Hülle, bleibt der Tour-Start
 * unberührt (nur das Schließen entfällt).
 */
function schliesseKontoMenue(): void {
  const huelle = document.getElementById(KONTO_AUSLOESER_ID)?.closest<HTMLElement>(".hs-dropdown");
  if (!huelle) return;
  void import("preline/non-auto")
    .then(({ HSDropdown }) => HSDropdown.close(huelle))
    .catch(() => {
      // Preline nicht verfügbar: das Menü bleibt offen, die Tour läuft trotzdem.
    });
}

export function TourWahl({ touren }: { touren: readonly KontoTour[] }) {
  let letzteGruppe: string | undefined;
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-account-and-workspace-menus/account-menu-with-status-controls (Untermenü, Einträge als Zeilen) */}
      {touren.map((t) => {
        const neueGruppe = t.gruppe !== undefined && t.gruppe !== letzteGruppe;
        letzteGruppe = t.gruppe;
        return (
          <div key={t.id}>
            {neueGruppe && <p className="px-3 pt-2 pb-1 text-xs font-medium text-muted-foreground-1">{t.gruppe}</p>}
            <button type="button" role="menuitem" className={EINTRAG_VOLL} onClick={() => {
                starteTour(t.id);
                schliesseKontoMenue();
              }}>
              {t.zusatz && <span className="shrink-0 text-xs text-muted-foreground-1">{t.zusatz}</span>}
              <span className="grow truncate" title={t.zusatz ? `${t.zusatz}: ${t.titel}` : t.titel}>
                {t.titel}
              </span>
            </button>
          </div>
        );
      })}
    </>
  );
}
